import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from 'recharts';
import { Users, Target, TrendingUp, Gauge } from 'lucide-react';
import { c, t, card } from '../../theme';

// Facility-level KPI tiles + a per-tier "Avg Rating vs Target" bar chart for
// a Mechanic Development section (Yard or MRF). kpis/tierSummary come
// pre-computed from the backend (staffing.js computeMechanicKpis /
// computeTierSummary) — aggregate numbers, not per-person PII, so this
// renders the same regardless of can_view_employees.
export default function MechanicMetrics({ kpis, tierSummary, title }) {
  if (!kpis || !kpis.total) return null;

  const chartRows = (tierSummary || []).filter((t) => t.avg_rating !== null);
  const height = Math.max(140, chartRows.length * 40 + 40);

  return (
    <div style={s.wrap}>
      <div style={s.kpiRow}>
        <StatTile icon={<Users size={15} />} label="Mechanics Scored"
                   value={`${kpis.scored_count} / ${kpis.total}`} />
        <StatTile icon={<Gauge size={15} />} label="Avg Rating"
                   value={kpis.avg_rating ?? '—'} />
        <StatTile icon={<TrendingUp size={15} />} label="Avg vs. Target"
                   value={kpis.avg_benchmark === null ? '—' : (kpis.avg_benchmark > 0 ? '+' : '') + kpis.avg_benchmark}
                   accent={kpis.avg_benchmark === null ? undefined : (kpis.avg_benchmark >= 0 ? c.successText : c.dangerText)} />
        <StatTile icon={<Target size={15} />} label="Meeting/Exceeding Target"
                   value={kpis.pct_meeting_expectation === null ? '—' : `${kpis.pct_meeting_expectation}%`} />
      </div>

      {chartRows.length > 0 && (
        <div style={s.chartCard}>
          <div style={s.chartTitle}>{title} — Avg Rating vs. Target by Role</div>
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={chartRows} layout="vertical" barGap={2} margin={{ top: 4, right: 24, bottom: 4, left: 8 }}>
              <CartesianGrid horizontal={false} stroke={c.canvas} />
              <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 11, fill: c.textFaint }} />
              <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 11, fill: c.textStrong }} interval={0} />
              <Tooltip
                cursor={{ fill: 'rgba(27,42,74,0.05)' }}
                contentStyle={{ fontSize: '12px', borderRadius: '8px', border: `1px solid ${c.border}` }}
                formatter={(v, name) => [v, name === 'target' ? 'Target' : 'Avg Rating']}
              />
              <Bar dataKey="target" fill={c.borderStrong} radius={[0, 4, 4, 0]} barSize={12} />
              <Bar dataKey="avg_rating" radius={[0, 4, 4, 0]} barSize={12}>
                {chartRows.map((r) => (
                  <Cell key={r.label} fill={r.avg_rating >= r.target ? c.success : c.danger} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div style={s.legend}>
            <LegendDot color={c.borderStrong} label="Target" />
            <LegendDot color={c.success} label="Meeting/Exceeding Target" />
            <LegendDot color={c.danger} label="Below Target" />
          </div>
        </div>
      )}
    </div>
  );
}

function StatTile({ icon, label, value, accent }) {
  return (
    <div style={s.tile}>
      <div style={s.tileIcon}>{icon}</div>
      <div>
        <div style={{ ...s.tileValue, ...(accent ? { color: accent } : {}) }}>{value}</div>
        <div style={s.tileLabel}>{label}</div>
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span style={s.legendItem}>
      <span style={{ ...s.legendDot, background: color }} />
      {label}
    </span>
  );
}

const s = {
  wrap:       { marginBottom: '16px' },
  kpiRow:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '12px' },
  tile:       { ...card.sm, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px' },
  tileIcon:   { width: '28px', height: '28px', borderRadius: '7px', background: c.canvas, color: c.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  tileValue:  { fontSize: '16px', fontWeight: 700, color: c.text },
  tileLabel:  { fontSize: t.micro.fontSize, color: c.textMuted, marginTop: '1px' },
  chartCard:  { ...card.sm, padding: '14px' },
  chartTitle: { fontSize: t.caption.fontSize, fontWeight: 600, color: c.textStrong, marginBottom: '4px' },
  legend:     { display: 'flex', flexWrap: 'wrap', gap: '14px', marginTop: '8px', paddingLeft: '4px' },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: t.micro.fontSize, color: c.textMuted },
  legendDot:  { width: '9px', height: '9px', borderRadius: '2px', display: 'inline-block' },
};
